import { faCheck, faEye } from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Container, Row, Spinner, Stack, Table } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { fetchAssessments, fetchAssessmentTypes, patchAssessment } from "../../../../services/httpClient";
import { Assessment } from "../../../../types/assessment";
import ToastMsg from "../../../ui/ToastMsg";
import AssessmentContinuum from "./AssessmentContinuum";
import AssessmentWellBeing from "./AssessmentWellBeing";
import { AssessmentType } from "../../../../types/assessmenttype";
import SelectObject from "../../../ui/SelectObject";
import AssessmentCustom from "./AssessmentCustom";
import IconEmbeddedButton from "../../../ui/IconEmbeddedButton";
import { Tooltip } from "react-tooltip";
import DatePicker from "../../../ui/DatePicker";
import IconButton from "../../../ui/IconButton";
import SelectFamily from "../../../ui/SelectFamily";

interface AssessmentsState {
  viewAssessments: boolean;
  setViewAssessments: any;
  createAssessmentWell: boolean;
  setCreateAssessmentWell: any;
  createAssessmentCont: boolean;
  setCreateAssessmentCont: any;
  createAssessmentCustom: boolean;
  setCreateAssessmentCustom: any;
  viewAssessmentWell: boolean;
  setViewAssessmentWell: any;
  viewAssessmentCont: boolean;
  setViewAssessmentCont: any;
  viewAssessmentCustom: boolean;
  setViewAssessmentCustom: any;
  getData: boolean;
  myCase: boolean;
  family: any;
}

const Assessments = ({
  viewAssessments,
  setViewAssessments,
  createAssessmentWell,
  setCreateAssessmentWell,
  createAssessmentCont,
  setCreateAssessmentCont,
  createAssessmentCustom,
  setCreateAssessmentCustom,
  viewAssessmentWell,
  setViewAssessmentWell,
  viewAssessmentCont,
  setViewAssessmentCont,
  viewAssessmentCustom,
  setViewAssessmentCustom,
  getData,
  myCase,
  family,
}: AssessmentsState) => {
  const { id } = useParams();
  const [selectedTypeKey, setSelectedTypeKey] = useState<string>("");
  const [selectedType, setSelectedType] = useState<AssessmentType>();
  const [assessDate, setAssessDate] = useState<string>("");
  const [publishedAssessmentTypes, setPublishedAssessmentTypes] = useState<AssessmentType[]>([]);
  //this includes archived and published
  const [allAssessmentTypes, setAllAssessmentTypes] = useState<AssessmentType[]>([]);
  const [completedAssessments, setCompletedAssessments] = useState<Assessment[]>([]);
  const [results, setResults] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [errMsg, setErrMsg] = useState<string>("");
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [currentSubject, setCurrentSubject] = useState<string>("unassigned");

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      getAssessmentTypes();
      getAssessments();
      setEditingRow(null);
    }
  }, [getData]);

  const getAssessmentTypes = () => {
    fetchAssessmentTypes()
      .then((res: any) => {
        var types = res[0].data;
        setAllAssessmentTypes(types);
        types = types.filter((t: any) => t?.is_archived !== 1 && t?.is_published === 1);
        setPublishedAssessmentTypes(types);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getAssessments = () => {
    fetchAssessments(id)
      .then((res: any) => {
        const assessments = res[0].data;
        setCompletedAssessments(assessments);
        setSelectedTypeKey("0");
        setSelectedType(undefined);
        setAssessDate(new Date().toISOString().split("T")[0]); // Set current date in YYYY-MM-DD format
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const familyMembers = useMemo(() => {
    const normalized = family.map((f: any) => ({
      id: f.id,
      type: f.role === "family" ? "guardian" : "member",
      key: `${f.firstname} ${f.lastname}`,
      name: `${f.firstname} ${f.lastname}`,
      isDeleted: f.deleted_at ? true : false,
      isBanned: f.is_banned === 1 ? true : false,
    }));

    return [...normalized].sort((a, b) => a.isDeleted - b.isDeleted);
  }, [family]);

  const activeFamily = useMemo(() => {
    const filtered = familyMembers.filter((f: any) => !f.isDeleted);
    return [...filtered].sort((a, b) => a.isBanned - b.isBanned);
  }, [familyMembers]);

  const updateAssessmentType = (e: any) => {
    setSelectedTypeKey(e.target.value);
    const assessmentType = publishedAssessmentTypes.filter((type: any) => type?.key === e.target.value);
    setSelectedType(assessmentType[0]);
  };

  const updateAssessmentDate = (date: any) => {
    setAssessDate(date);
  };

  const updateViewAssessments = (e: any) => {
    setErrMsg("");
    setSelectedTypeKey("0");
    setSelectedType(undefined);
    setAssessDate(new Date().toISOString().split("T")[0]); // Set current date in YYYY-MM-DD format
    setViewAssessments(e);
    setCreateAssessmentWell(false);
    setCreateAssessmentCont(false);
    setCreateAssessmentCustom(false);
    setViewAssessmentWell(false);
    setViewAssessmentCont(false);
    setViewAssessmentCustom(false);
  };

  const onClickCreateAssessment = () => {
    setErrMsg("");
    setCreateAssessmentWell(false);
    setCreateAssessmentCont(false);
    setCreateAssessmentCustom(false);

    if (selectedTypeKey === "well_being") {
      setCreateAssessmentWell(true);
    } else if (selectedTypeKey === "continuum") {
      setCreateAssessmentCont(true);
    } else {
      setCreateAssessmentCustom(true);
    }

    setViewAssessments(false);
    setViewAssessmentWell(false);
    setViewAssessmentCont(false);
    setViewAssessmentCustom(false);
  };

  const createDueAssessment = (e: any, key: string, h: any) => {
    e.stopPropagation();

    setCreateAssessmentWell(false);
    setCreateAssessmentCont(false);
    setCreateAssessmentCustom(false);

    setSelectedTypeKey(key);
    const assessmentType = publishedAssessmentTypes.filter((type: any) => type?.key === key);
    setSelectedType(assessmentType[0]);
    setAssessDate(new Date().toISOString().split("T")[0]); // Set current date in YYYY-MM-DD format

    const subjectString = h?.subject ? `${h.subject.id}|${h.subject.type}` : "unassigned";
    setCurrentSubject(subjectString);

    if (key === "well_being") {
      setCreateAssessmentWell(true);
    } else if (key === "continuum") {
      setCreateAssessmentCont(true);
    } else {
      setCreateAssessmentCustom(true);
    }

    setViewAssessments(false);
    setViewAssessmentWell(false);
    setViewAssessmentCont(false);
    setViewAssessmentCustom(false);
  };

  const onClickViewAssessment = (item: any) => {
    const assessmentType = allAssessmentTypes.filter((type: any) => type?.key === item.assessmenttypekey);
    setSelectedType(assessmentType[0]);

    setViewAssessments(false);
    setCreateAssessmentWell(false);
    setCreateAssessmentCont(false);
    setCreateAssessmentCustom(false);

    if (item.assessmenttypekey === "continuum") {
      setViewAssessmentWell(false);
      setViewAssessmentCont(true);
      setViewAssessmentCustom(false);
    } else if (item.assessmenttypekey === "well_being") {
      setViewAssessmentWell(true);
      setViewAssessmentCont(false);
      setViewAssessmentCustom(false);
    } else {
      //need to account for custom
      setViewAssessmentWell(false);
      setViewAssessmentCont(false);
      setViewAssessmentCustom(true);
    }

    setResults(item);
  };

  const onClickUpdateSubject = (e: any) => {
    var subject = {};
    if (e.target.value === "unassigned") {
      subject = {
        id: 0,
      };
    } else {
      const [subjectId, subjectType] = e.target.value.split("|");
      subject = {
        id: subjectId,
        type: subjectType,
      };
    }

    patchAssessment(subject, id, editingRow)
      .then((res: any) => {
        setToastText("Assessment Updated");
        setToastBody("Member Updated");
        setShowToast(true);
        setEditingRow(null);
        getAssessments();
      })
      .catch((err: any) => {
        console.error(err);
        setErrMsg("Failed to update household member");
      });
  };

  const onClickEditSubject = (assessmentId: number) => {
    setEditingRow(assessmentId);
    setErrMsg("");
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />

      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {viewAssessments === true && (
            <>
              <Row className="mb-0">
                <Col xs={12}>
                  <h4 className="app-header-text">Assessments</h4>
                </Col>
              </Row>
              {myCase && (
                <Row>
                  <Col xs={12} md={10} lg={10} className="text-end">
                    <Stack direction="horizontal" gap={3}>
                      <Col xs={7} md={6} lg={6}>
                        <SelectObject
                          testId="assessment-select"
                          label="Assessment"
                          value={selectedTypeKey}
                          options={publishedAssessmentTypes}
                          onUpdate={(e: any) => updateAssessmentType(e)}
                        />
                      </Col>
                      <Col xs={3} md={3} lg={3}>
                        <DatePicker label="Date" currentValue={assessDate} setCurrentValue={updateAssessmentDate} hasMaxDate={true} />
                      </Col>
                      <Button
                        className="app-primary-color button"
                        onClick={() => onClickCreateAssessment()}
                        disabled={selectedType && assessDate ? false : true}
                      >
                        Create
                      </Button>
                    </Stack>
                  </Col>
                </Row>
              )}
              <Table striped>
                {completedAssessments.length === 0 ? (
                  <p className="mt-2">No Completed Assessment Records</p>
                ) : (
                  <thead>
                    <tr>
                      <th>Assessment Name</th>
                      <th>Completed Date</th>
                      <th>Household Member</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                )}
                <tbody className="align-items-center">
                  {completedAssessments?.map((h: any) => (
                    <tr key={h.id} /*onClick={(e: any) => onClickViewAssessment(h)}*/>
                      <td>{h.assessmenttype}</td>
                      <td>{new Date(h.assessment_at).toLocaleDateString()}</td>
                      <td className="align-middle">
                        {editingRow === h.id ? (
                          <Stack direction="horizontal" gap={3}>
                            <SelectFamily
                              family={activeFamily}
                              familyUser={h?.subject ? `${h.subject.id}|${h.subject.type}` : "unassigned"}
                              updateFamilyUser={onClickUpdateSubject}
                              readOnly={myCase ? false : true}
                              includeLabel={false}
                              defaultSelection="Unassigned"
                            />
                            <IconButton onClickButton={() => setEditingRow(null)} tooltipText={"Close"} icon={faCheck} />
                          </Stack>
                        ) : (
                          <>
                            <Badge
                              data-tooltip-id="tooltip"
                              data-tooltip-content={"Edit Member"}
                              className="app-primary-color cursor-pointer"
                              bg="none"
                              onClick={() => onClickEditSubject(h?.id)}
                            >
                              {h?.subject?.name || "Unassigned"}
                            </Badge>
                            <Tooltip id="tooltip" place="top" style={{ padding: "5px 10px" }} />
                          </>
                        )}
                      </td>
                      <td className="align-middle">
                        {myCase &&
                        h.most_recent_due &&
                        !h.is_archived &&
                        (() => {
                          const type = allAssessmentTypes.find((t: any) => t.id === h.assessmenttype_id);
                          return type && type.is_published === 1;
                        })() ? (
                          <>
                            <Badge
                              data-tooltip-id="tooltip"
                              data-tooltip-content={"Create Today"}
                              className="app-tertiary-color cursor-pointer"
                              bg="none"
                              onClick={(e: any) => createDueAssessment(e, h.assessmenttypekey, h)}
                            >
                              Due
                            </Badge>
                            <Tooltip id="tooltip-badge" place="top" style={{ padding: "5px 10px" }} />
                          </>
                        ) : (
                          <></>
                        )}
                      </td>
                      <td>
                        <IconEmbeddedButton
                          onClickButton={(e: any) => onClickViewAssessment(h)}
                          tooltipText="View Assessment"
                          icon={faEye}
                          color="primary"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}

          {createAssessmentWell === true && (
            <>
              <Row className="mb-0">
                <Col>
                  <h4 className="app-header-text">Well Being Assessment</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => updateViewAssessments(true)}>
                    Cancel
                  </Button>
                </Col>
              </Row>
              <AssessmentWellBeing
                setViewAssessments={setViewAssessments}
                setCreateAssessmentWell={setCreateAssessmentWell}
                setCreateAssessmentCont={setCreateAssessmentCont}
                setCreateAssessmentCustom={setCreateAssessmentCustom}
                fetchAssessments={getAssessments}
                family={activeFamily}
                questions={selectedType}
                assessDate={assessDate}
                subject={currentSubject}
                setShowToast={setShowToast}
                setToastText={setToastText}
                setToastBody={setToastBody}
                setErrMsg={setErrMsg}
              />
            </>
          )}

          {createAssessmentCont === true && (
            <>
              <Row className="mb-0">
                <Col>
                  <h4 className="app-header-text">Independence Continuum</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => updateViewAssessments(true)}>
                    Cancel
                  </Button>
                </Col>
              </Row>
              <AssessmentContinuum
                setViewAssessments={setViewAssessments}
                setCreateAssessmentWell={setCreateAssessmentWell}
                setCreateAssessmentCont={setCreateAssessmentCont}
                setCreateAssessmentCustom={setCreateAssessmentCustom}
                fetchAssessments={getAssessments}
                family={activeFamily}
                assessDate={assessDate}
                subject={currentSubject}
                setShowToast={setShowToast}
                setToastText={setToastText}
                setToastBody={setToastBody}
                setErrMsg={setErrMsg}
              />
            </>
          )}

          {createAssessmentCustom === true && (
            <>
              <Row className="mb-0">
                <Col>
                  <h4 className="app-header-text">{selectedType?.name}</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => updateViewAssessments(true)}>
                    Cancel
                  </Button>
                </Col>
              </Row>
              <AssessmentCustom
                setViewAssessments={setViewAssessments}
                setCreateAssessmentWell={setCreateAssessmentWell}
                setCreateAssessmentCont={setCreateAssessmentCont}
                setCreateAssessmentCustom={setCreateAssessmentCustom}
                fetchAssessments={getAssessments}
                family={activeFamily}
                questions={selectedType}
                assessDate={assessDate}
                subject={currentSubject}
                setShowToast={setShowToast}
                setToastText={setToastText}
                setToastBody={setToastBody}
                setErrMsg={setErrMsg}
              />
            </>
          )}

          {viewAssessmentWell === true && (
            <>
              <Row className="mb-3">
                <Col>
                  <h4 className="app-header-text">Well Being Assessment</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => updateViewAssessments(true)}>
                    Back
                  </Button>
                </Col>
              </Row>
              <AssessmentWellBeing
                setViewAssessments={setViewAssessments}
                setCreateAssessmentWell={setCreateAssessmentWell}
                setCreateAssessmentCont={setCreateAssessmentCont}
                setCreateAssessmentCustom={setCreateAssessmentCustom}
                fetchAssessments={getAssessments}
                family={activeFamily}
                readOnly={true}
                questions={selectedType}
                results={results}
                subject={results?.subject?.id + "|" + results?.subject?.type}
                setErrMsg={setErrMsg}
              />
            </>
          )}

          {viewAssessmentCont === true && (
            <>
              <Row className="mb-3">
                <Col>
                  <h4 className="app-header-text">Independence Continuum</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => updateViewAssessments(true)}>
                    Back
                  </Button>
                </Col>
              </Row>
              <AssessmentContinuum
                setViewAssessments={setViewAssessments}
                setCreateAssessmentWell={setCreateAssessmentWell}
                setCreateAssessmentCont={setCreateAssessmentCont}
                setCreateAssessmentCustom={setCreateAssessmentCustom}
                fetchAssessments={getAssessments}
                family={activeFamily}
                readOnly={true}
                results={results}
                subject={results?.subject?.id + "|" + results?.subject?.type}
                setErrMsg={setErrMsg}
              />
            </>
          )}

          {viewAssessmentCustom === true && (
            <>
              <Row className="mb-3">
                <Col>
                  <h4 className="app-header-text">{selectedType?.name}</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => updateViewAssessments(true)}>
                    Back
                  </Button>
                </Col>
              </Row>
              <AssessmentCustom
                setViewAssessments={setViewAssessments}
                setCreateAssessmentWell={setCreateAssessmentWell}
                setCreateAssessmentCont={setCreateAssessmentCont}
                setCreateAssessmentCustom={setCreateAssessmentCustom}
                fetchAssessments={getAssessments}
                family={activeFamily}
                questions={selectedType}
                readOnly={true}
                results={results}
                subject={results?.subject?.id + "|" + results?.subject?.type}
                setErrMsg={setErrMsg}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default Assessments;
